import CloseIcon from "@mui/icons-material/Close";
import { env } from "@/config/env";

interface HelpPanelProps {
  open: boolean;
  onClose: () => void;
}

const HELP_SECTIONS: { title: string; items: string[] }[] = [
  {
    title: "Navigation",
    items: [
      "Use the sidebar for Dashboard, Operations, Reports, and Setup.",
      "Switch apps from the launcher in the header (POS, CRM, HR, Finance, and more).",
      "Pin a page with the star icon in the header to add it to Favorites.",
    ],
  },
  {
    title: "Search & shortcuts",
    items: [
      "Press Ctrl+K (Cmd+K on Mac) to search customers, products, leads, invoices, employees, and tickets.",
      "Press Shift+? to open this help panel.",
      "On the POS terminal, use the Help button for billing keyboard shortcuts.",
    ],
  },
  {
    title: "Organization",
    items: [
      "One organization can subscribe to multiple Fattah applications.",
      "Roles and permissions control who sees each module and screen.",
      "Workspace settings and billing live under Administration.",
    ],
  },
];

export function HelpPanel({ open, onClose }: HelpPanelProps) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[55] flex justify-end">
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        aria-label="Close help"
        onClick={onClose}
      />
      <aside
        className="relative flex h-full w-full max-w-md flex-col border-l border-slate-200 bg-white shadow-panel dark:border-slate-700 dark:bg-slate-900"
        role="dialog"
        aria-labelledby="help-panel-title"
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-700">
          <h2
            id="help-panel-title"
            className="text-base font-semibold text-slate-900 dark:text-slate-100"
          >
            Help
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="workspace-header-icon-btn"
            aria-label="Close"
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {env.appName} workspace
          </h3>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
            Quick guide for navigating your organization, switching
            applications, and finding records.
          </p>
          {HELP_SECTIONS.map((section) => (
            <div key={section.title} className="mt-6">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
                {section.title}
              </p>
              <ul className="mt-2 list-disc space-y-2 pl-5 text-sm text-slate-600 dark:text-slate-300">
                {section.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          ))}
          <p className="mt-8 text-xs text-slate-400">
            Need more detail? See documentation under{" "}
            <code className="text-xs">docs/modules/</code> in the project
            repository.
          </p>
        </div>
      </aside>
    </div>
  );
}
